/**
 * Prices vs the last bid — a report (owner, Nov 1 2026: "a report of clients whose app price doesn't match their
 * last quote (e.g. Mauricio Galdieri $70 vs $35, Adrian $140 vs $45). Report only, don't change any prices").
 *
 * Every active mowing yard: what the next mow bills, what the last accepted mowing bid promised per mow, the gap
 * and a line on why. Mismatches first, biggest gap first; each row opens the customer and the bid. A sheet (CSV).
 * Nothing on this page changes a price. Server: routes/priceCheck.js → services/priceCheck.
 */
import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Scale, Download, ExternalLink } from 'lucide-react';
import { api } from '../services/api';

type Mow = { job_id: number; date: string; price: number | null; href: string };
type Bid = { quote_id: number; title: string | null; status: string; accepted_at: string | null; per_mow: number | null; mow_price: number | null; frequency: string | null;
  extras: { label: string; amount: number }[]; first_fees: number; href: string };
type Row = {
  schedule_id: number; customer_id: number; customer_name: string; is_commercial: boolean; property_id: number | null; address: string | null; frequency: string | null;
  schedule_price: number | null; yard_price: number | null; next_mow: Mow | null; last_mow: Mow | null; bill_price: number | null; bill_from: string | null;
  discount: { amount: number; label: string } | null; credit_owed: number | null; monthly_price: number | null; bid: Bid | null;
  mismatch: boolean; gap: number | null; why: string; href: string;
};
type Data = { today: string; rows: Row[]; counts: { yards: number; mismatched: number; matched: number; no_bid: number; covered: number }; words: string };

const money = (n: number | null | undefined) => (n == null ? '—' : `$${(Math.round(Number(n) * 100) / 100).toLocaleString('en-US', { minimumFractionDigits: Number(n) % 1 ? 2 : 0, maximumFractionDigits: 2 })}`);
const day = (iso: string | null | undefined) => (iso ? new Date(`${String(iso).slice(0, 10)}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const CADENCE: Record<string, string> = { weekly: 'Weekly', biweekly: 'Every other week', monthly: 'Monthly', custom: 'Custom' };
const FILTERS: { key: string; label: string; of: (r: Row) => boolean }[] = [
  { key: 'mismatch', label: 'Mismatches', of: r => r.mismatch },
  { key: 'all', label: 'Every yard', of: () => true },
  { key: 'match', label: 'Matches', of: r => !r.mismatch && !!r.bid && !r.monthly_price },
  { key: 'no_bid', label: 'No bid', of: r => !r.bid },
];

export default function PriceCheck() {
  const [filter, setFilter] = useState('mismatch');
  const { data, isLoading, isError } = useQuery<Data>('price-check', () => api.get('/price-check').then(r => r.data), { staleTime: 30_000 });
  async function exportCSV() {
    try {
      const res = await api.get('/price-check.csv', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url; a.download = `prices-vs-last-bid-${data?.today || ''}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Could not make the sheet'); }
  }
  const all = data?.rows || [];
  const rows = all.filter(FILTERS.find(f => f.key === filter)?.of || (() => true));
  const counts = data?.counts;
  const countOf = (key: string) => all.filter(FILTERS.find(f => f.key === key)?.of || (() => true)).length;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-gray-900 flex items-center gap-2"><Scale className="h-7 w-7 text-brand-700" aria-hidden /> Prices vs last bid</h1>
          <p className="text-sm text-gray-600 mt-1">{data ? data.words : 'What the next mow bills against what the last accepted bid promised.'}</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/billing" className="press inline-flex items-center px-3 py-2 text-sm font-semibold rounded-xl border border-gray-300 bg-white hover:bg-gray-50">Billing</Link>
          <button type="button" onClick={exportCSV} className="press inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-xl border border-gray-300 bg-white hover:bg-gray-50"><Download className="h-4 w-4" aria-hidden /> Sheet (CSV)</button>
        </div>
      </div>

      <div className="card p-4 text-sm text-gray-700 space-y-1">
        <p><span className="font-semibold">Bill price</span> is what the next planned mow will bill (its own price, the one month-end uses); when no mow is planned, the schedule's price, else the yard's.
          <span className="font-semibold"> Bid price</span> is the mow line of the newest accepted mowing bid — the mow alone, not the per-visit extras.</p>
        <p>A gap over 50¢ is a mismatch, after allowing for a standing discount, the first-mow fees and the recurring credit on the third mow.
          This page only reports — nothing here changes a price. Open the customer or the bid to fix one.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map(f => (
          <button key={f.key} type="button" onClick={() => setFilter(f.key)}
            className={`press px-3 py-1.5 rounded-full text-sm font-semibold border ${filter === f.key ? 'bg-brand-700 text-white border-brand-700' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
            {f.label}{data ? ` (${countOf(f.key)})` : ''}
          </button>
        ))}
      </div>

      {isLoading && <p className="text-sm text-gray-500">Reading the prices…</p>}
      {isError && <p className="text-sm text-red-700">Could not read the prices.</p>}
      {data && !rows.length && (
        <div className="card p-6 text-center text-gray-600">
          {filter === 'mismatch' ? `Every yard with a bid matches it${counts?.no_bid ? ` · ${counts.no_bid} with no accepted bid` : ''}.` : 'Nothing here.'}
        </div>
      )}

      {rows.length > 0 && (
        <div className="card p-0 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2">Customer</th>
                <th className="px-3 py-2 text-right">Bill price</th>
                <th className="px-3 py-2 text-right">Bid price</th>
                <th className="px-3 py-2 text-right">Gap</th>
                <th className="px-3 py-2 text-right">Schedule</th>
                <th className="px-3 py-2 text-right">Yard</th>
                <th className="px-3 py-2">Next mow</th>
                <th className="px-3 py-2">Last mow</th>
                <th className="px-3 py-2">Bid</th>
                <th className="px-3 py-2">Why</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map(r => (
                <tr key={r.schedule_id} className={r.mismatch ? 'bg-amber-50/60' : ''}>
                  <td className="px-3 py-2 align-top">
                    <Link to={r.href} className="font-semibold text-gray-900 hover:underline">{r.customer_name}</Link>
                    {r.is_commercial && <span className="ml-1.5 text-[10px] uppercase tracking-wide text-gray-500 border border-gray-300 rounded px-1">Business</span>}
                    <div className="text-xs text-gray-500">{r.address || 'No address'} · {CADENCE[r.frequency || ''] || r.frequency || '—'}</div>
                    {r.discount && <div className="text-xs text-green-800">{money(r.discount.amount)} off every mow — {r.discount.label}</div>}
                    {r.credit_owed != null && <div className="text-xs text-gray-500">{money(r.credit_owed)} credit still owed on the third mow</div>}
                  </td>
                  <td className="px-3 py-2 align-top text-right font-semibold tabular-nums">
                    {r.monthly_price ? <>{money(r.monthly_price)}<span className="text-xs text-gray-500">/mo</span></> : money(r.bill_price)}
                    {!r.monthly_price && r.bill_from && r.bill_from !== 'next mow' && <div className="text-[11px] font-normal text-gray-500">from the {r.bill_from}</div>}
                  </td>
                  <td className="px-3 py-2 align-top text-right tabular-nums">
                    {r.bid ? <>{money(r.bid.per_mow)}{r.bid.extras?.length ? <div className="text-[11px] text-gray-500">+ {r.bid.extras.map(e => `${e.label} ${money(e.amount)}`).join(', ')}</div> : null}</> : <span className="text-gray-400">no bid</span>}
                  </td>
                  <td className={`px-3 py-2 align-top text-right tabular-nums font-semibold ${r.mismatch ? (Number(r.gap) > 0 ? 'text-amber-800' : 'text-red-700') : 'text-gray-400'}`}>
                    {r.gap == null ? '—' : `${Number(r.gap) > 0 ? '+' : ''}${money(r.gap)}`}
                  </td>
                  <td className="px-3 py-2 align-top text-right tabular-nums text-gray-700">{money(r.schedule_price)}</td>
                  <td className="px-3 py-2 align-top text-right tabular-nums text-gray-700">{money(r.yard_price)}</td>
                  <td className="px-3 py-2 align-top whitespace-nowrap">
                    {r.next_mow ? <Link to={r.next_mow.href} className="hover:underline">{day(r.next_mow.date)} · {money(r.next_mow.price)}</Link> : <span className="text-gray-400">none planned</span>}
                  </td>
                  <td className="px-3 py-2 align-top whitespace-nowrap">
                    {r.last_mow ? <Link to={r.last_mow.href} className="hover:underline">{day(r.last_mow.date)} · {money(r.last_mow.price)}</Link> : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-3 py-2 align-top whitespace-nowrap">
                    {r.bid ? <Link to={r.bid.href} className="inline-flex items-center gap-1 hover:underline">{day(r.bid.accepted_at)} <ExternalLink className="h-3 w-3" aria-hidden /></Link> : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-3 py-2 align-top text-gray-700 min-w-[16rem]">{r.why || (r.bid ? 'matches' : '')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
